/**
 * The organization's shared directory of coaches, scouts and other contacts.
 *
 * Mirrors `DTOs/TrustedContactDtos.cs` and `Enums/TrustedContactType.cs` in the API — the enum is
 * serialized as a string there, so these literals are the wire values, not display copy.
 */

/** Mirrors `Enums/TrustedContactType.cs`. Wire values, not labels — see `CONTACT_TYPE_LABELS`. */
export const TRUSTED_CONTACT_TYPES = [
  "CollegeCoach",
  "JucoCoach",
  "HighSchoolCoach",
  "TravelCoach",
  "Scout",
] as const;

export type TrustedContactType = (typeof TRUSTED_CONTACT_TYPES)[number];

export interface TrustedContact {
  id: string;
  name: string;
  organizationName: string | null;
  /**
   * Free text, up to 50 characters, following the same convention as `organizationName`.
   * Optional because this shipped after the rest of the record — a build talking to an API
   * from before this field existed omits the key entirely rather than sending it as null. Render
   * with `?? "—"` (or similar), never assume the key is present.
   */
  state?: string | null;
  contactType: TrustedContactType;
  /** What the coach typed, for display. */
  phone: string | null;
  /**
   * Canonical E.164, or null when the stored number could not be parsed as one. This — not
   * `phone` — is what decides whether a contact can be texted: a directory row can carry a
   * number that displays fine and cannot be dialled.
   */
  phoneE164: string | null;
  email: string | null;
  notes: string | null;
  /**
   * Normalised to a real boolean at the service boundary (`normalizeTrustedContact` in
   * `services/trusted-contacts.ts`) — an API from before this field existed omits the key, and
   * `undefined` must read as "not favorited", never crash a `!contact.isFavorite` check.
   */
  isFavorite: boolean;
  createdAt: string;
  updatedAt: string;
}

/** The fields a coach fills in, on add and on edit alike. */
export interface TrustedContactInput {
  name: string;
  organizationName?: string | null;
  /** Free text, up to 50 characters. Sent as `null`, never `""`, when left blank. */
  state?: string | null;
  contactType: TrustedContactType;
  phone?: string | null;
  email?: string | null;
  notes?: string | null;
}

export interface TrustedContactQuery {
  search?: string;
  contactType?: TrustedContactType | null;
  organization?: string | null;
  /**
   * States to include, any of them. Empty or omitted means no narrowing — a coach recruiting a
   * region asks about several at once, so this is a list rather than the single value it started
   * as. Each becomes its own `state` query parameter.
   */
  states?: string[];
  /** Omitted (rather than sent `false`) when the coach has not asked to narrow to favorites. */
  isFavorite?: boolean;
  pageNumber?: number;
  pageSize?: number;
}

/** One address-book entry the import skipped because the directory already held its number. */
export interface DuplicateTrustedContact {
  name: string;
  existingContact: TrustedContact;
}

export interface ImportTrustedContactsResult {
  imported: TrustedContact[];
  duplicates: DuplicateTrustedContact[];
  /** Entries with no name, or with neither a dialable number nor an email. */
  skipped: string[];
  importedCount: number;
  duplicateCount: number;
}

/**
 * One row as read out of a spreadsheet.
 *
 * Distinct from {@link TrustedContactInput} because `contactType` is genuinely absent when the file
 * had no type column, or held a word we could not resolve. The coach picks a default for those
 * before committing, so the required-type shape is only correct on the far side of that choice —
 * saying so here is what stops the default being forgotten.
 */
export interface ParsedTrustedContact extends Omit<TrustedContactInput, "contactType"> {
  contactType?: TrustedContactType | null;
}

/**
 * What reading a coach's spreadsheet produced.
 *
 * Nothing has been written at this point — the coach checks these rows, and which column was read
 * as what, and then commits through the ordinary import.
 */
export interface ParseTrustedContactsCsvResult {
  contacts: ParsedTrustedContact[];
  /** Source header → the field it was read as, so the coach can spot a column read wrongly. */
  mapping: Record<string, string>;
  /** Things worth knowing that did not stop the parse, such as unrecognised contact types. */
  warnings: string[];
  error: string | null;
  contactCount: number;
}

/**
 * What removing a selection actually did.
 *
 * `skippedIds` holds contacts the server did not act on — already removed by someone else while
 * the page sat open, most likely. A partial batch is a 200 with a report, not an error; the
 * reasoning lives with the decision, in TrustedContactRepository.SoftDeleteManyAsync.
 */
export interface BulkDeleteTrustedContactsResult {
  deletedCount: number;
  deletedIds: string[];
  skippedIds: string[];
}
